/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { Platform } from 'react-native';
import {
  MAP_EDIT_RETURN_URL_KEY,
  MAP_SEARCH_RETURN_AVAILABLE_KEY,
} from '../configs/constants';
import {
  hasFreeBreakfastSearchTag,
  removeFreeBreakfastFromRecord,
} from './roomFilters';

const HOTEL_SEARCH_PARAMS_STORAGE_KEY = 'hotel_search_params';

export const getStoredSearchParams = (): Record<string, string> | null => {
  if (typeof window === 'undefined' || Platform.OS !== 'web') return null;

  try {
    const stored = sessionStorage.getItem(HOTEL_SEARCH_PARAMS_STORAGE_KEY);
    return stored ? (JSON.parse(stored) as Record<string, string>) : null;
  } catch {
    return null;
  }
};

const stringifySearchParams = (params: Record<string, unknown>) => {
  const stringParams: Record<string, string> = {};

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    // Skip non-URL values from parseHotelSearchParams (empty filter arrays,
    // parsedRooms objects, etc.) — String([]) is "" and String(object) is
    // "[object Object]", both of which can corrupt map/search query strings.
    if (Array.isArray(value)) {
      if (
        value.length === 0 ||
        value.some((item) => item !== null && typeof item === 'object')
      ) {
        return;
      }
      stringParams[key] = value.map(String).filter(Boolean).join(',');
      return;
    }

    if (typeof value === 'object') {
      return;
    }

    if (typeof value === 'boolean') {
      stringParams[key] = value ? 'true' : 'false';
      return;
    }

    const stringValue = String(value).trim();
    if (!stringValue || stringValue === '[object Object]') {
      return;
    }

    stringParams[key] = stringValue;
  });

  return stringParams;
};

export const storeSearchParams = (params: Record<string, unknown>) => {
  if (typeof window === 'undefined' || Platform.OS !== 'web') return;

  try {
    sessionStorage.setItem(
      HOTEL_SEARCH_PARAMS_STORAGE_KEY,
      JSON.stringify(stringifySearchParams(params)),
    );
  } catch {
    // ignore
  }
};

export const clearStoredSearchParams = () => {
  if (typeof window === 'undefined' || Platform.OS !== 'web') return;

  try {
    sessionStorage.removeItem(HOTEL_SEARCH_PARAMS_STORAGE_KEY);
  } catch {
    // ignore
  }
};

export const markMapSearchReturnAvailable = (): void => {
  if (typeof window === 'undefined' || Platform.OS !== 'web') return;

  try {
    sessionStorage.setItem(MAP_SEARCH_RETURN_AVAILABLE_KEY, '1');
  } catch {
    // ignore
  }
};

/** Peek without consuming — used to force search form UI on first paint. */
export const isMapSearchReturnAvailable = (): boolean => {
  if (typeof window === 'undefined' || Platform.OS !== 'web') return false;

  try {
    return sessionStorage.getItem(MAP_SEARCH_RETURN_AVAILABLE_KEY) === '1';
  } catch {
    return false;
  }
};

export const consumeMapSearchReturnAvailability = (): boolean => {
  if (typeof window === 'undefined' || Platform.OS !== 'web') return false;

  try {
    const isAvailable =
      sessionStorage.getItem(MAP_SEARCH_RETURN_AVAILABLE_KEY) === '1';
    if (isAvailable) {
      sessionStorage.removeItem(MAP_SEARCH_RETURN_AVAILABLE_KEY);
    }
    return isAvailable;
  } catch {
    return false;
  }
};

/** Saves the current map URL so search header-back can restore it exactly. */
export const markMapEditReturnUrl = (): void => {
  if (typeof window === 'undefined' || Platform.OS !== 'web') return;

  try {
    const returnUrl = window.location.href;
    if (returnUrl) {
      sessionStorage.setItem(MAP_EDIT_RETURN_URL_KEY, returnUrl);
    }
  } catch {
    // ignore
  }
};

/** Reads and clears the saved map return URL. */
export const consumeMapEditReturnUrl = (): string | null => {
  if (typeof window === 'undefined' || Platform.OS !== 'web') return null;

  try {
    const returnUrl = sessionStorage.getItem(MAP_EDIT_RETURN_URL_KEY);
    if (returnUrl) {
      sessionStorage.removeItem(MAP_EDIT_RETURN_URL_KEY);
    }
    return returnUrl;
  } catch {
    return null;
  }
};

/** Clears transient map-edit navigation markers from session storage. */
export const clearMapViewNavigationOrigin = (): void => {
  if (typeof window === 'undefined' || Platform.OS !== 'web') return;

  try {
    sessionStorage.removeItem(MAP_SEARCH_RETURN_AVAILABLE_KEY);
    sessionStorage.removeItem(MAP_EDIT_RETURN_URL_KEY);
    const stored = getStoredSearchParams();
    if (!stored?.from_mapview && !stored?.v) {
      return;
    }

    const { from_mapview: _fromMapview, v: _v, ...rest } = stored;
    if (Object.keys(rest).length > 0) {
      storeSearchParams(rest);
      return;
    }

    sessionStorage.removeItem(HOTEL_SEARCH_PARAMS_STORAGE_KEY);
  } catch {
    // ignore
  }
};

/**
 * Removes free breakfast search parameters from session storage if present.
 */
export const removeFreeBreakfastFromStoredSearchParams = (): void => {
  if (typeof window === 'undefined' || Platform.OS !== 'web') return;

  try {
    const stored = getStoredSearchParams();
    if (!stored) return;

    if (hasFreeBreakfastSearchTag(stored)) {
      const updated = removeFreeBreakfastFromRecord(stored);
      if (Object.keys(updated).length > 0) {
        storeSearchParams(updated);
      } else {
        sessionStorage.removeItem(HOTEL_SEARCH_PARAMS_STORAGE_KEY);
      }
    }
  } catch {
    // ignore
  }
};
